import { Box } from "@mui/material";
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
} from "@mui/x-data-grid";
import type { SxProps, Theme } from "@mui/material/styles";
import { useMemo, type ReactElement } from "react";

/**
 * `<DataTable>` — Apple-clean MUI X DataGrid wrapper.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 3".
 *
 * Behaviour you get for free:
 * - 3-dot column menu on every non-pinned header (Sort asc/desc, Hide,
 *   Manage columns) — the user's Story 060 ask.
 * - Native sort indicators (▲ / ▼).
 * - Column resize by dragging the right edge of a header.
 * - Column reorder by dragging the header.
 * - Built-in keyboard nav.
 * - ARIA grid roles (`role="grid" / "row" / "columnheader" / "cell"`).
 *
 * Defaults:
 * - `border: none` — the page-shell column-flex region owns the visual
 *   chrome.
 * - `hideFooter: true` — the canonical `<DataPageFooter>` is rendered
 *   below the grid by the page.
 * - `disableRowSelectionOnClick: true`, `paginationMode: "server"`,
 *   `sortingMode: "server"`, `pageSizeOptions: [25, 50, 100]`.
 * - `cursor: pointer` on rows when `onRowClick` is set.
 *
 * `pinnedActionColumn` — when true, the LAST column gets
 * `disableColumnMenu: true` and stays visible regardless of column-
 * visibility model state (the page-level visibility menu hides it from
 * its options; the DataGrid's own 3-dot menu skips this column).
 */
export interface DataTableProps<R extends { id: string | number }>
  extends Omit<DataGridProps<R>, "columns"> {
  columns: readonly GridColDef<R>[];
  ariaLabel: string;
  /** When true, the last column is treated as a pinned action column —
   *  its column menu is suppressed and it is forced always-visible in
   *  the column-visibility model. */
  pinnedActionColumn?: boolean;
}

export function DataTable<R extends { id: string | number }>({
  columns,
  ariaLabel,
  pinnedActionColumn,
  onRowClick,
  columnVisibilityModel,
  sx,
  ...rest
}: DataTableProps<R>): ReactElement {
  const effectiveColumns = useMemo<GridColDef<R>[]>(() => {
    if (!pinnedActionColumn || columns.length === 0) {
      return [...columns];
    }
    const head = columns.slice(0, -1);
    const last = columns[columns.length - 1];
    if (!last) return [...columns];
    return [
      ...head,
      {
        ...last,
        disableColumnMenu: true,
        // Pinned action columns are intentionally not sortable / not
        // filterable. Callers can still override via the column def if
        // they really need to.
        sortable: last.sortable ?? false,
        filterable: last.filterable ?? false,
      },
    ];
  }, [columns, pinnedActionColumn]);

  // Force the pinned action column always-visible by clamping its key in
  // the visibility model to `true` regardless of what the caller passes.
  const effectiveVisibilityModel = useMemo(() => {
    if (!pinnedActionColumn || effectiveColumns.length === 0) {
      return columnVisibilityModel;
    }
    const last = effectiveColumns[effectiveColumns.length - 1];
    if (!last) return columnVisibilityModel;
    return {
      ...(columnVisibilityModel ?? {}),
      [last.field]: true,
    };
  }, [columnVisibilityModel, pinnedActionColumn, effectiveColumns]);

  return (
    <Box sx={{ position: "relative", flex: 1, minWidth: 0, minHeight: 0 }}>
      <DataGrid<R>
        columns={effectiveColumns}
        aria-label={ariaLabel}
        // MUI X v7 places the DataGrid's `aria-label` on the OUTER
        // `MuiDataGrid-root` div, not on the inner `role="grid"`
        // element. To make `getByRole("grid", { name: <ariaLabel> })`
        // match — which the acceptance suites depend on — we pipe the
        // label into the `slotProps.main` slot, which spreads onto
        // GridMainContainer (the role="grid" carrier).
        slotProps={{
          main: { "aria-label": ariaLabel },
        }}
        disableRowSelectionOnClick
        hideFooter
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[25, 50, 100]}
        {...(onRowClick ? { onRowClick } : {})}
        {...(effectiveVisibilityModel
          ? { columnVisibilityModel: effectiveVisibilityModel }
          : {})}
        sx={
          [
            {
              // Borderless inside the page-shell column-flex region.
              border: "none",
              "& .MuiDataGrid-row": onRowClick
                ? { cursor: "pointer" }
                : undefined,
              // MUI X v7 cells default to `display: block`, which top-
              // aligns custom `renderCell` content (Stacks, Typography,
              // chips). Force flex-center so every cell — text, chip, or
              // icon-button — sits on the row's vertical midline. Spec:
              // `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 3"
              // (vertical centering rule).
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            },
            // Caller-provided sx — flatten through MUI's array overload.
            ...(Array.isArray(sx)
              ? (sx as unknown[])
              : sx !== undefined
                ? [sx]
                : []),
          ] as unknown as SxProps<Theme>
        }
        {...rest}
      />
    </Box>
  );
}
